import { useState } from "react";
import { PageHeader, useResource } from "@/admin/shared";
import { Button, Field, Input, Modal, StatusPill } from "@/admin/ui";
import { api, formatApiError } from "@/admin/api";

export default function Billing() {
    const { data, loading, refresh } = useResource("/plans");
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    function openEdit(p) {
        setEditing(p);
        setForm({ ...p });
        setErr("");
    }

    async function save(e) {
        e?.preventDefault?.();
        setBusy(true);
        setErr("");
        try {
            await api.patch(`/plans/${editing.id}`, {
                name: form.name,
                monthly_price: Number(form.monthly_price),
                user_limit: Number(form.user_limit),
                company_limit: Number(form.company_limit),
                agent_limit: Number(form.agent_limit),
                scan_limit: Number(form.scan_limit),
                decision_limit: Number(form.decision_limit),
                storage_limit_gb: Number(form.storage_limit_gb),
                status: form.status || "active",
            });
            setEditing(null);
            await refresh();
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div data-testid="admin-billing-page">
            <PageHeader
                title="Billing & Plans"
                subtitle="Configure the plans available to companies — limits, pricing, and usage caps."
            />

            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="glass rounded-3xl p-6 h-[320px] animate-pulse" />
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {(data || []).map((p, i) => (
                        <div
                            key={p.id}
                            data-testid={`plan-${p.key}`}
                            className={`rounded-3xl p-6 flex flex-col ${
                                p.key === "growth"
                                    ? "glass-strong glow-purple ring-1 ring-violet-200"
                                    : "glass"
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <span className={`text-[10px] font-semibold tracking-[0.22em] uppercase rounded-full px-2.5 py-1 ${p.key === "growth" ? "bg-violet-600 text-white" : "bg-neutral-100 text-neutral-700"}`}>
                                    {p.name}
                                </span>
                                <StatusPill status={p.status} />
                            </div>
                            <div className="mt-6">
                                <div className="font-display text-4xl font-medium text-black leading-none">
                                    ₹{(p.monthly_price || 0).toLocaleString()}
                                </div>
                                <div className="mt-1 text-[12px] text-neutral-500">/ month</div>
                            </div>
                            <ul className="mt-6 space-y-2 text-[13px] text-neutral-700">
                                <Row k="Users" v={p.user_limit} />
                                <Row k="Companies" v={p.company_limit} />
                                <Row k="AI Agents" v={p.agent_limit} />
                                <Row k="Scans / mo" v={p.scan_limit} />
                                <Row k="Decision Cards / mo" v={p.decision_limit} />
                                <Row k="Storage" v={`${p.storage_limit_gb} GB`} />
                            </ul>
                            <div className="mt-auto pt-6">
                                <Button variant="ghost" size="md" className="w-full justify-center" onClick={() => openEdit(p)} data-testid={`plan-edit-${p.id}`}>
                                    Configure plan
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Modal
                open={!!editing}
                onClose={() => setEditing(null)}
                title={editing ? `Edit ${editing.name} plan` : ""}
                size="lg"
                testid="plan-modal"
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                        <Button onClick={save} disabled={busy} data-testid="plan-save-btn">
                            {busy ? "Saving…" : "Save"}
                        </Button>
                    </>
                }
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Name"><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                    <Field label="Monthly Price (₹)"><Input type="number" min="0" value={form.monthly_price || 0} onChange={(e) => setForm({ ...form, monthly_price: e.target.value })} /></Field>
                    <Field label="User Limit"><Input type="number" min="1" value={form.user_limit || 0} onChange={(e) => setForm({ ...form, user_limit: e.target.value })} /></Field>
                    <Field label="Company Limit"><Input type="number" min="1" value={form.company_limit || 0} onChange={(e) => setForm({ ...form, company_limit: e.target.value })} /></Field>
                    <Field label="Agent Limit"><Input type="number" min="1" value={form.agent_limit || 0} onChange={(e) => setForm({ ...form, agent_limit: e.target.value })} /></Field>
                    <Field label="Scan Limit / mo"><Input type="number" min="0" value={form.scan_limit || 0} onChange={(e) => setForm({ ...form, scan_limit: e.target.value })} /></Field>
                    <Field label="Decision Card Limit / mo"><Input type="number" min="0" value={form.decision_limit || 0} onChange={(e) => setForm({ ...form, decision_limit: e.target.value })} /></Field>
                    <Field label="Storage (GB)"><Input type="number" min="0" value={form.storage_limit_gb || 0} onChange={(e) => setForm({ ...form, storage_limit_gb: e.target.value })} /></Field>
                    {err && (
                        <div className="sm:col-span-2 rounded-xl border border-rose-200 bg-rose-50/70 text-rose-700 text-[13px] px-3.5 py-2.5">
                            {err}
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
}

function Row({ k, v }) {
    return (
        <li className="flex items-center justify-between">
            <span className="text-neutral-500">{k}</span>
            <span className="font-semibold text-black">{v}</span>
        </li>
    );
}
